import { useState, useEffect, useMemo, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Form, FormRenderProps } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import equal from 'fast-deep-equal';
import { v4 as uuidv4 } from 'uuid';
import type { Dispatch } from 'redux';

import { getUserRoleIsProsecutorOrSuperProsecutor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { IChecklistTemplateQuestion } from 'APIPackage/AccountModule/interfaces/IChecklistTemplateQuestion';
import { IUserOrganisation } from 'APIPackage/AccountModule/interfaces/IUserOrganisation';
import { Button } from 'UIPackage/MainModule/components/Button';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { makeMaxCharactersValidator } from 'CorePackage/MainModule/validators/maxCharacters';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { DragDropList, DragDropListItem, DragHandle } from 'UIPackage/MainModule/components/DragDropList';
import { IconMenu } from 'UIPackage/IconsModule/components/IconMenu';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { deleteObjectProperties } from 'CorePackage/MainModule/common/deleteObjectProperties';
import { convertOrganisationToSelectOption } from 'PersonalAccountPackage/MainModule/common/convertOrganisationToSelectOption';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import styles from './CheckListForm.module.scss';
import { IFormChecklistTemplateQuestion } from '../interfaces/IFormChecklistTemplateQuestion';
import { ICheckListForm } from '../interfaces/ICheckListForm';
import { checkListsFormSubmit } from '../actions/checkListsAction';

const PAGE_SIZE = 50;

interface ICheckListFormProps {
  templateName?: string;
  approvalDetails?: string;
  organisations: IUserOrganisation[];
  activeOrganisationId?: string;
  questions?: IChecklistTemplateQuestion[];
  isReset: boolean;
  onReset: () => void;
  onSubmit?: (values: ICheckListForm) => void;
  onQuestionDelete?: (name: string, onUndo: () => void) => void;
}

interface ICheckListFormStoreProps {
  isProsecutorOrSuperProsecutor: boolean;
}

const CheckListFormWithoutStore = ({
  templateName = '',
  approvalDetails = '',
  organisations,
  activeOrganisationId,
  questions = [],
  isReset,
  onReset,
  onQuestionDelete,
  onSubmit,
  isProsecutorOrSuperProsecutor,
}: ICheckListFormProps & ICheckListFormStoreProps) => {
  const [questionsPage, setQuestionsPage] = useState<number>(0);
  let resetForm: (() => void) | undefined;
  const dispatch = useDispatch<Dispatch<RootActions>>();

  useEffect(() => {
    if (isReset) {
      if (resetForm) {
        resetForm();
      }
      onReset();
    }
  }, [isReset, onReset, resetForm]);

  useEffect(() => {
    if (resetForm) {
      resetForm();
    }
    setQuestionsPage(0);
  }, [templateName, resetForm]);

  const organisationsSelectOptions: IUserOrganisationSelectOption[] = organisations.map(
    convertOrganisationToSelectOption,
  );

  const organisationsSelectInitialValue = organisationsSelectOptions.find(option => option.id === activeOrganisationId);

  const initialValues: ICheckListForm = useMemo(
    () => ({
      templateName,
      approvalDetails,
      organisation: organisationsSelectInitialValue,
      questions,
    }),
    [approvalDetails, organisationsSelectInitialValue, questions, templateName],
  );

  const handleFormSubmit = useCallback(
    (values: ICheckListForm) => {
      dispatch(checkListsFormSubmit(values, initialValues, onSubmit, resetForm));
    },
    [dispatch, initialValues, onSubmit, resetForm],
  );

  const handleSortEnd = (move: (from: number, to: number) => void) => ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    move(oldIndex, newIndex);
  };

  const lastVisibleQuestionNumber = (questionsPage + 1) * PAGE_SIZE;
  const isLoadButtonVisible = questions.length > lastVisibleQuestionNumber;

  const handleLoadButtonClick = useCallback(() => setQuestionsPage(prev => prev + 1), []);

  function renderForm({ handleSubmit, form }: FormRenderProps<ICheckListForm>) {
    if (!resetForm && form && form.reset) {
      resetForm = () => {
        form.setConfig('keepDirtyOnReinitialize', false);
        form.reset();
        form.setConfig('keepDirtyOnReinitialize', true);
      };
    }
    const { push, update } = form.mutators;

    function addControlQuestion() {
      if (form.getFieldState('questions')?.value?.length === lastVisibleQuestionNumber) {
        setQuestionsPage(questionsPage + 1);
      }
      push('questions', {
        newId: uuidv4(),
      });
    }

    const makeRemoveControlQuestionHandler = (index: number, data: IFormChecklistTemplateQuestion) => () => {
      const { id, newId, question } = data;
      const deleteId = id || id === 0 ? `${id}` : newId;

      update('questions', index, {
        ...data,
        deleteId,
      });
      const undo = () => {
        const currentQuestionsValue = form.getFieldState('questions')?.value;
        const undoIndex = currentQuestionsValue?.findIndex(
          (item: IFormChecklistTemplateQuestion) => item.deleteId === deleteId,
        );

        if (typeof undoIndex === 'number' && currentQuestionsValue?.[undoIndex]) {
          update(
            'questions',
            undoIndex,
            deleteObjectProperties<IFormChecklistTemplateQuestion, keyof IFormChecklistTemplateQuestion>(
              currentQuestionsValue?.[undoIndex],
              ['deleteId'],
            ),
          );
        }
      };

      if (onQuestionDelete) {
        onQuestionDelete(question, undo);
      }
    };

    function handleCancelButtonClick() {
      form.setConfig('keepDirtyOnReinitialize', false);
      form.reset();
      form.setConfig('keepDirtyOnReinitialize', true);
    }

    return (
      <form onSubmit={handleSubmit}>
        <Row>
          <FieldBlock label="Наименование" color="black" placementLabel="left" labelSize="col-4">
            <TextInputField
              disabled={isProsecutorOrSuperProsecutor}
              name="templateName"
              className="shared-row-field"
              placeholder="Наименование проверочного листа"
              validate={composeValidators(
                makeRequired('Поле не может быть пустым'),
                makeMaxCharactersValidator(512, 'Превышена допустимая длина строки'),
              )}
            />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock label="Организация" color="black" placementLabel="left" labelSize="col-4">
            <SelectInputField
              disabled={isProsecutorOrSuperProsecutor}
              name="organisation"
              id="organisation"
              className="shared-row-field"
              options={organisationsSelectOptions}
              validate={composeValidators(makeRequired('Поле не может быть пустым'))}
            />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock label="Сведения об утверждении" color="black" placementLabel="left" labelSize="col-4">
            <TextInputField
              disabled={isProsecutorOrSuperProsecutor}
              name="approvalDetails"
              className="shared-row-field"
              validate={composeValidators(
                makeRequired('Поле не может быть пустым'),
                makeMaxCharactersValidator(2048, 'Превышена допустимая длина строки'),
              )}
            />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock
            label="Контрольные вопросы"
            button={
              <Button
                className="shared-btn-add"
                text="Добавить"
                variant="link"
                color="primary"
                type="button"
                id="questionAddButton"
                disabled={isProsecutorOrSuperProsecutor}
                onClick={addControlQuestion}
              />
            }
          ></FieldBlock>
        </Row>
        <FieldArray name="questions" isEqual={equal}>
          {({ fields }) => (
            <DragDropList
              lockAxis="y"
              lockToContainerEdges
              lockOffset="0%"
              onSortEnd={handleSortEnd(fields.move)}
              useDragHandle
            >
              <ul className={styles.Questions}>
                {fields.map((name, index) => {
                  const { id, newId, deleteId } = fields.value[index];

                  if (deleteId || index > lastVisibleQuestionNumber - 1) {
                    return null;
                  }
                  const fieldId = id || id === 0 ? `${id}` : newId;

                  return (
                    <DragDropListItem key={fieldId} index={index} disabled={isProsecutorOrSuperProsecutor}>
                      <li className={styles.QuestionBlock}>
                        <DragHandle>
                          <div className={styles.QuestionMove} hidden>
                            <IconMenu className={styles.QuestionMoveIcon} />
                          </div>
                        </DragHandle>
                        <div className={styles.QuestionFields}>
                          <Row notWrap notSpace>
                            <FieldBlock margin="right">
                              <TextareaField
                                disabled={isProsecutorOrSuperProsecutor}
                                name={`${name}.question`}
                                placeholder="Вопрос"
                                resize="none"
                                autoHeight
                                validate={composeValidators(
                                  makeRequired('Поле не может быть пустым'),
                                  makeMaxCharactersValidator(4096, 'Превышена допустимая длина строки'),
                                )}
                              />
                            </FieldBlock>
                            <FieldBlock>
                              <TextareaField
                                disabled={isProsecutorOrSuperProsecutor}
                                name={`${name}.npaProps`}
                                placeholder="Реквизиты НПА устанавливающего обязательные требования"
                                resize="none"
                                autoHeight
                                validate={composeValidators(
                                  makeRequired('Поле не может быть пустым'),
                                  makeMaxCharactersValidator(4096, 'Превышена допустимая длина строки'),
                                )}
                              />
                            </FieldBlock>
                          </Row>
                          <input disabled={isProsecutorOrSuperProsecutor} name={`${name}.id`} type="hidden" />
                        </div>
                        {!isProsecutorOrSuperProsecutor && (
                          <CloseButton
                            className={styles.QuestionDeleteButton}
                            onClick={makeRemoveControlQuestionHandler(index, fields.value[index])}
                          />
                        )}
                      </li>
                    </DragDropListItem>
                  );
                })}
              </ul>
            </DragDropList>
          )}
        </FieldArray>
        {isLoadButtonVisible && (
          <Button type="button" text="Показать еще" color="load" onClick={handleLoadButtonClick} />
        )}
        {!isProsecutorOrSuperProsecutor && (
          <div className="shared-action-buttons">
            <Button variant="contained" color="primary" type="submit" text="Сохранить" id="saveButton" />
            <Button
              className="shared-action-cancel"
              variant="contained"
              color="default"
              type="button"
              text="Отменить"
              onClick={handleCancelButtonClick}
            />
          </div>
        )}
      </form>
    );
  }

  return (
    <Form<ICheckListForm>
      onSubmit={handleFormSubmit}
      render={renderForm}
      initialValues={initialValues}
      keepDirtyOnReinitialize
      mutators={{ ...arrayMutators }}
      validateOnBlur={false}
    />
  );
};

const mapStateToProps = (state: IRootState): ICheckListFormStoreProps => ({
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
});

export const CheckListForm = connect(mapStateToProps)(CheckListFormWithoutStore);
